import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, Globe } from "lucide-react";
import { useLang, LANGS } from "@/context/LanguageContext";

const scrollTo = (id) => {
  const target = `#${id}`;
  if (window.__lenis) {
    window.__lenis.scrollTo(target, { offset: -20, duration: 1.4 });
  } else {
    document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
  }
};

const Navbar = () => {
  const { lang, setLang, t, theme, setTheme } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["hero", t.nav.maison],
    ["manifeste", t.nav.manifeste],
    ["residents", t.nav.residents],
    ["bureau", t.nav.bureau],
    ["evenements", t.nav.evenements],
    ["boutique", t.nav.boutique],
    ["journal", t.nav.journal],
    ["galerie", t.nav.galerie],
    ["contact", t.nav.contact],
  ];

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <>
      <motion.header
        data-testid="main-navbar"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${
          scrolled ? "bg-ink/80 backdrop-blur-xl border-b border-gold/15" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-3.5 sm:px-8 lg:px-16">
          <button
            data-testid="nav-logo-button"
            onClick={() => go("hero")}
            className="group flex items-center gap-3 text-left"
          >
            <img src="/images/logo.png" alt="Maison pour l'Art et la Science" className="h-10 w-10 object-contain" />
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-sand sm:block">
              Maison pour l'Art<br />et la Science
            </span>
          </button>

          <nav className="hidden items-center gap-5 xl:flex">
            {links.map(([id, label]) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => go(id)}
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-parchment/70 transition-colors duration-300 hover:text-gold"
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-theme-toggle"
              aria-label={theme === "parchment" ? t.nav.theme_to_dark : t.nav.theme_to_light}
              title={theme === "parchment" ? t.nav.theme_to_dark : t.nav.theme_to_light}
              onClick={() => setTheme(theme === "parchment" ? "nuit" : "parchment")}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-gold/25 bg-ink2/60 text-gold backdrop-blur-md transition-colors duration-300 hover:border-gold/60"
            >
              {theme === "parchment" ? <Moon size={15} /> : <Sun size={15} />}
            </button>
            <div className="relative" data-testid="nav-language-toggle">
              <button
                data-testid="nav-lang-button"
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 rounded-full border border-gold/25 bg-ink2/60 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold backdrop-blur-md transition-colors duration-300 hover:border-gold/60"
              >
                <Globe size={13} /> {lang}
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    data-testid="nav-lang-menu"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-12 z-50 max-h-80 w-48 overflow-y-auto border border-gold/25 bg-ink2/95 backdrop-blur-xl"
                  >
                    {LANGS.map((l) => (
                      <button
                        key={l.code}
                        data-testid={`nav-lang-${l.code}`}
                        onClick={() => {
                          setLang(l.code);
                          setLangOpen(false);
                        }}
                        className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-sm font-light transition-colors ${
                          lang === l.code ? "bg-gold/15 text-gold" : "text-sand hover:bg-gold/10 hover:text-parchment"
                        }`}
                      >
                        {l.label}
                        <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-sand/50">{l.code}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button
              data-testid="nav-menu-button"
              aria-label={open ? t.nav.close_menu : t.nav.open_menu}
              onClick={() => setOpen(!open)}
              className="rounded-full border border-gold/25 p-2 text-parchment xl:hidden"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-30 flex flex-col items-center justify-center gap-7 bg-ink/95 backdrop-blur-2xl xl:hidden"
          >
            {links.map(([id, label], i) => (
              <motion.button
                key={id}
                data-testid={`mobile-nav-link-${id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 * i, duration: 0.5 }}
                onClick={() => go(id)}
                className="font-serif text-2xl text-parchment transition-colors hover:text-gold sm:text-3xl"
              >
                {label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
